import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import api from '../utils/api'; // <-- use your axios instance here
import { EXPIRY_NOTICE_KEY } from '../utils/session';
import { getSafeReturnPath } from '../utils/safeRedirect';
import { useTranslation } from 'react-i18next';
import { applyLoginLanguage } from '../utils/languageOverride';
import { translateKnown } from '../i18n/knownMessages';
import GoogleSignInButton from '../components/GoogleSignInButton';
import SiteFooter from '../components/common/SiteFooter';
import { saveLoginSession } from '../utils/saveLoginSession';

export default function Login() {
  const [identifier, setIdentifier] = useState(''); // email or username
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Google credential waiting for the password of an existing account with the same email
  const [pendingGoogle, setPendingGoogle] = useState(null);
  const [linkPassword, setLinkPassword] = useState('');
  const [linkPasswordError, setLinkPasswordError] = useState('');
  const [isLinking, setIsLinking] = useState(false);
  // Expiry notice from automatic logout: shown once, then removed so a refresh does not repeat it
  const [notice, setNotice] = useState(() => sessionStorage.getItem(EXPIRY_NOTICE_KEY));
  useEffect(() => { sessionStorage.removeItem(EXPIRY_NOTICE_KEY); }, []);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const from = getSafeReturnPath(location.state?.from);
  // Already signed in: go to the default page instead of showing the form
  const signedIn = !!localStorage.getItem('user');

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setNotice(null);
    sessionStorage.removeItem(EXPIRY_NOTICE_KEY);

    try {
      const res = await api.post('/api/users/login', { identifier, password });
      const data = res.data;

      // store token and user info
      localStorage.setItem('token', data.token);
      localStorage.setItem(
        'user',
        JSON.stringify({
          userId: data.userId,
          username: data.username,
          isAdmin: data.isAdmin,
          role: data.role,
          delegatedGroupCount: data.delegatedGroupCount || 0,
          avatar: data.avatar || null,
        })
      );
      localStorage.setItem('userId', data.userId);
      // A saved account language wins over the guest choice; without one, the guest choice stays
      applyLoginLanguage(data.language);

      navigate(from, { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      // axios error handling: check if response exists
      const message =
        err.response?.data?.message || 'Login failed. Check credentials.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogle = async (credential) => {
    setError('');
    setNotice(null);
    sessionStorage.removeItem(EXPIRY_NOTICE_KEY);
    try {
      const res = await api.post('/api/users/google', { credential });
      saveLoginSession(res.data);
      navigate(from, { replace: true });
    } catch (err) {
      // Same email as a password account: ask for its password before linking Google
      if (err.response?.data?.code === 'google_link_required') {
        setPendingGoogle(credential);
        setLinkPassword('');
        return;
      }
      setError(err.response?.data?.message || 'Google sign-in failed. Please try again.');
    }
  };

  const handleLinkGoogle = async (e) => {
    e.preventDefault();
    // Empty password: show the message beside the field and send nothing
    if (!linkPassword) {
      setLinkPasswordError(t('password.required'));
      return;
    }
    setIsLinking(true);
    setError('');
    try {
      const res = await api.post('/api/users/google/link', { credential: pendingGoogle, password: linkPassword });
      setLinkPassword('');
      saveLoginSession(res.data);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Google sign-in failed. Please try again.');
    } finally {
      setIsLinking(false);
    }
  };

  if (signedIn) return <Navigate to={from} replace />;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md bg-white p-8 rounded-lg shadow-md border border-gray-200">
        <h2 className="text-2xl font-semibold text-center text-gray-800 mb-6">
          {t('auth.login')}
        </h2>

        {!error && notice && (
          <div className="text-amber-700 text-center mb-4">
            {notice === 'idle'
              ? t('messages.sessionIdle')
              : t('messages.sessionExpired')}
          </div>
        )}
        {error && <div className="text-red-500 text-center mb-4">{translateKnown(t, error)}</div>}

        <form onSubmit={handleLogin} className="space-y-4" noValidate>
          <div>
            <label htmlFor="identifier" className="block font-semibold mb-1">
              {t('auth.usernameOrEmail')}
            </label>
            <input
              id="identifier"
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
              className="border p-2 rounded w-full"
              autoComplete="username"
            />
          </div>

          <div>
            <label htmlFor="password" className="block font-semibold mb-1">
              {t('auth.password')}
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="border p-2 rounded w-full pr-10"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-800"
                aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <div className="text-right text-sm">
            <Link to="/forgot-password" className="text-blue-600 hover:underline">
              {t('auth.forgotPassword')}
            </Link>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-2 rounded text-white transition ${
              isSubmitting
                ? 'bg-green-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {isSubmitting ? t('auth.loggingIn') : t('auth.logIn')}
          </button>

          <button
            type="button"
            onClick={() => navigate('/register')}
            className="w-full bg-gray-200 text-gray-800 py-2 rounded hover:bg-gray-300 transition"
          >
            {t('auth.register')}
          </button>
        </form>
        {pendingGoogle ? (
          <form onSubmit={handleLinkGoogle} className="mt-4 space-y-4" noValidate>
            <p className="text-sm text-gray-700">{t('messages.googleLinkPrompt')}</p>
            <div>
              <label htmlFor="linkPassword" className="block font-semibold mb-1">
                {t('messages.confirmExistingPassword')}
              </label>
              <input
                id="linkPassword"
                type="password"
                value={linkPassword}
                onChange={(e) => { setLinkPassword(e.target.value); setLinkPasswordError(''); }}
                required
                aria-invalid={linkPasswordError ? 'true' : undefined}
                aria-describedby={linkPasswordError ? 'linkPassword-error' : undefined}
                className="border p-2 rounded w-full"
                autoComplete="current-password"
              />
              {linkPasswordError && (
                <p id="linkPassword-error" className="text-red-500 text-sm mt-1">{linkPasswordError}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={isLinking}
              className={`w-full py-2 rounded text-white transition ${
                isLinking ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {isLinking ? t('messages.linking') : t('messages.linkGoogle')}
            </button>
            <button
              type="button"
              onClick={() => { setPendingGoogle(null); setLinkPassword(''); setLinkPasswordError(''); setError(''); }}
              className="w-full bg-gray-200 text-gray-800 py-2 rounded hover:bg-gray-300 transition"
            >
              {t('profile.cancel')}
            </button>
          </form>
        ) : (
          <div className="mt-4">
            <GoogleSignInButton onCredential={handleGoogle} onError={setError} />
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
